import React from 'react';
import PropTypes from 'prop-types';
import {
  LuBriefcase,
  LuCalendarClock,
  LuCalendarPlus,
  LuCheck,
  LuChevronLeft,
  LuChevronRight,
  LuCircleHelp,
  LuExternalLink,
  LuFileSpreadsheet,
  LuInfo,
  LuListChecks,
  LuPlay,
} from 'react-icons/lu';
import { useTranslation } from '../../../../../i18n/LanguageContext';
import { abs, typed } from '../../engine/stage';
import { Abs, Btn, CARD, Field, GLASS_CHIP, Note, SectionHead, Segmented, TILE } from '../kit';

// ── Takvim (short tour; the calendar has its own video) ─────────────────
export const CAL = (top) => ({
  prev: { x: 256, y: top, w: 36, h: 36 },
  next: { x: 446, y: top, w: 36, h: 36 },
  views: { x: 790, y: top, w: 110, h: 36 },
  excel: { x: 912, y: top, w: 92, h: 36 },
  addJob: { x: 1010, y: top, w: 110, h: 36 },
  addVisit: { x: 1126, y: top, w: 130, h: 36 },
  actions: { x: 912, y: top, w: 344, h: 36 },
  summary: { x: 256, y: top + 48, w: 452, h: 28 },
  help: { x: 982, y: top + 48, w: 140, h: 28 },
  video: { x: 1128, y: top + 48, w: 128, h: 28 },
  grid: { x: 256, y: top + 92, w: 700, h: 316 },
  cell: (row, col) => ({ x: 256 + col * 100, y: top + 92 + 26 + row * 72, w: 100, h: 72 }),
  panel: { x: 968, y: top + 92, w: 288, h: 316 },
});

const BARS = [
  { row: 0, from: 1, to: 3, tone: 'tw-bg-emerald-500/25 tw-border-emerald-500', label: 'ONKO-301 · V2' },
  { row: 1, from: 2, to: 5, tone: 'tw-bg-amber-400/30 tw-border-amber-500', label: 'KARDİYO-12 · V5' },
  { row: 2, from: 0, to: 2, tone: 'tw-bg-sky-500/20 tw-border-sky-500', label: 'ONKO-301 · V3' },
  { row: 2, from: 4, to: 6, tone: 'tw-bg-sky-500/20 tw-border-sky-500', label: 'NÖRO-7 · V1', lane: 1 },
  { row: 3, from: 3, to: 3, tone: 'tw-bg-violet-500/20 tw-border-violet-500', label: 'İş', job: true },
];

export const CalendarPane = ({ top, state }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const C = CAL(top);
  const selected = state.selected || { row: 1, col: 3 };
  const days = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const base = new Date();
  const monday = new Date(base.getFullYear(), base.getMonth(), 1);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return (
    <>
      <Btn rect={C.prev} icon={LuChevronLeft} />
      <span style={{ left: 300, top: top + 6, width: 140 }} className="tw-absolute tw-text-center tw-text-xl tw-font-semibold tw-text-slate-900 dark:tw-text-white">
        {base.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
      </span>
      <Btn rect={C.next} icon={LuChevronRight} />
      <Segmented rect={C.views} value="month" small options={[{ value: 'month', label: label('views.month'), w: 49 }, { value: 'list', label: label('views.list'), w: 49 }]} />
      <Btn rect={C.excel} icon={LuFileSpreadsheet}>{label('actions.excel')}</Btn>
      <Btn rect={C.addJob} icon={LuBriefcase}>{label('actions.addJob')}</Btn>
      <Btn rect={C.addVisit} variant="primary" icon={LuCalendarPlus}>{label('actions.addVisit')}</Btn>
      <Abs rect={C.summary} className="tw-flex tw-items-center tw-gap-2">
        {[['total', 8, 'tw-bg-slate-400'], ['planned', 4, 'tw-bg-sky-500'], ['inProgress', 2, 'tw-bg-amber-500'], ['completed', 2, 'tw-bg-emerald-500']].map(([key, count, dot]) => (
          <span key={key} className={`tw-inline-flex tw-h-7 tw-items-center tw-gap-1.5 tw-rounded-full tw-px-2.5 tw-text-xs tw-text-slate-600 dark:tw-text-slate-300 ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-bg-none`}>
            <span className={`tw-h-2 tw-w-2 tw-rounded-full ${dot}`} />
            <b className="tw-tabular-nums">{count}</b> {label(`summary.${key}`)}
          </span>
        ))}
      </Abs>
      <Btn rect={C.help} icon={LuCircleHelp} className="tw-h-7 tw-text-xs">{t('profilePage.calendar.help.button')}</Btn>
      <span style={abs(C.video)} className="tw-absolute tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-gradient-to-r tw-from-teal-500 tw-to-cyan-600 tw-pl-1 tw-pr-3 tw-text-xs tw-font-semibold tw-text-white tw-shadow-md tw-shadow-teal-500/30">
        <span className="tw-grid tw-h-5 tw-w-5 tw-place-items-center tw-rounded-full tw-bg-white tw-text-teal-600"><LuPlay aria-hidden="true" className="tw-ml-px tw-h-3 tw-w-3" /></span>
        {t('profilePage.tutorial.cta.button')}
      </span>
      <Abs rect={C.grid} className={`tw-overflow-hidden ${CARD}`}>
        <div className="tw-grid tw-h-[26px] tw-grid-cols-7 tw-items-center tw-border-b tw-border-slate-200/70 tw-text-center tw-text-[10px] tw-font-semibold tw-uppercase tw-text-slate-400 dark:tw-border-white/10">
          {days.map((day, index) => <span key={day}>{new Date(2024, 0, 1 + index).toLocaleDateString(locale, { weekday: 'short' })}</span>)}
        </div>
      </Abs>
      {Array.from({ length: 28 }, (_, index) => {
        const row = Math.floor(index / 7);
        const col = index % 7;
        const date = new Date(monday);
        date.setDate(monday.getDate() + index);
        const isSelected = row === selected.row && col === selected.col;
        return (
          <span key={index} style={abs(C.cell(row, col))} className={`tw-absolute tw-border-b tw-border-r tw-border-slate-100 tw-p-1.5 tw-text-[11px] dark:tw-border-white/5 ${isSelected ? 'tw-bg-teal-500/[0.07] tw-ring-2 tw-ring-inset tw-ring-teal-500/60' : ''}`}>
            <span className={`tw-inline-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-font-semibold ${isSelected ? 'tw-bg-teal-500 tw-text-white' : 'tw-text-slate-500'}`}>{date.getDate()}</span>
          </span>
        );
      })}
      {BARS.map((bar) => {
        const cell = C.cell(bar.row, bar.from);
        return (
          <span key={`${bar.row}-${bar.from}`} style={{ left: cell.x + 4, top: cell.y + 28 + (bar.lane || 0) * 20, width: (bar.to - bar.from + 1) * 100 - 8, height: 17 }} className={`tw-absolute tw-truncate tw-rounded tw-border-l-[3px] tw-px-1.5 tw-text-[10px] tw-font-semibold tw-leading-[17px] tw-text-slate-700 dark:tw-text-slate-200 ${bar.tone}`}>
            {bar.label}
          </span>
        );
      })}
      <Abs rect={C.panel} className={`tw-p-4 ${CARD}`}>
        <p className="tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{t('userGuide.stage.dayTitle')}</p>
        <p className="tw-mt-3 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400">{t('userGuide.stage.plannedToday')}</p>
        <div className={`tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 ${TILE}`}>
          <span className="tw-h-8 tw-w-1 tw-rounded-full tw-bg-amber-500" />
          <span className="tw-min-w-0 tw-flex-1">
            <span className="tw-block tw-truncate tw-text-[13px] tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">KARDİYO-12 · P-0877</span>
            <span className="tw-block tw-text-[11px] tw-text-slate-500">V5 · {label('summary.inProgress')}</span>
          </span>
        </div>
        <div className={`tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 ${TILE}`}>
          <span className="tw-h-8 tw-w-1 tw-rounded-full tw-bg-sky-500" />
          <span className="tw-min-w-0 tw-flex-1">
            <span className="tw-block tw-truncate tw-text-[13px] tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">ONKO-301 · P-1042</span>
            <span className="tw-block tw-text-[11px] tw-text-slate-500">V3 · {label('summary.planned')}</span>
          </span>
        </div>
      </Abs>
    </>
  );
};

CalendarPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };

// ── Ajandam ─────────────────────────────────────────────────────────────
export const AGENDA = (top) => ({
  google: { x: 1076, y: top + 4, w: 180, h: 34 },
  form: { x: 256, y: top + 70, w: 700, h: 360 },
  title: { x: 280, y: top + 118, w: 652, h: 40 },
  description: { x: 280, y: top + 194, w: 652, h: 60 },
  start: { x: 280, y: top + 290, w: 318, h: 40 },
  end: { x: 614, y: top + 290, w: 318, h: 40 },
  submit: { x: 280, y: top + 360, w: 652, h: 42 },
  side: { x: 968, y: top + 70, w: 288, h: 300 },
  opened: { x: 256, y: top + 442, w: 700, h: 58 },
});

export const AgendaPane = ({ top, state }) => {
  const { t } = useTranslation();
  const label = (key) => t(`profilePage.agenda.${key}`);
  const A = AGENDA(top);
  const p = state.p || 0;
  const filled = state.filled || 0;
  const title = filled >= 1 ? typed(t('userGuide.stage.agendaTitle'), p, state.typeFrom ?? 0, state.typeTo ?? 0.3) : '';
  return (
    <>
      <SectionHead top={top} icon={LuCalendarClock} gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30" title={label('title')} description={label('description')} />
      <Btn rect={A.google} icon={LuExternalLink} className="tw-text-xs">{label('openCalendar')}</Btn>
      <Abs rect={A.form} className={CARD} />
      <Field rect={A.title} label={label('eventTitle')} required value={state.cleared ? '' : (filled >= 2 ? t('userGuide.stage.agendaTitle') : title)} placeholder={label('eventTitlePlaceholder')} focused={state.focus === 'title'} />
      <Field rect={A.description} label={label('eventDescription')} area value={state.cleared ? '' : (filled >= 2 ? t('userGuide.stage.agendaDesc') : '')} placeholder={label('eventDescriptionPlaceholder')} focused={state.focus === 'description'} />
      <Field rect={A.start} label={label('start')} required value={!state.cleared && filled >= 3 ? '15.10.2026 10:00' : ''} placeholder="gg.aa.yyyy --:--" focused={state.focus === 'start'} />
      <Field rect={A.end} label={label('end')} required value={!state.cleared && filled >= 3 ? '15.10.2026 11:00' : ''} placeholder="gg.aa.yyyy --:--" focused={state.focus === 'end'} />
      <Btn rect={A.submit} variant="primary" icon={LuCalendarClock} pressed={state.pressed}>{label('add')}</Btn>
      <Abs rect={A.side} className={`tw-p-4 ${CARD}`}>
        <p className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100"><LuListChecks aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600" />{label('howTo')}</p>
        <ol className="tw-mt-3 tw-space-y-2.5">
          {['fill', 'add', 'save', 'done'].map((step, index) => (
            <li key={step} className="tw-flex tw-gap-2.5 tw-text-[13px] tw-leading-snug tw-text-slate-600 dark:tw-text-slate-300">
              <span className="tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-teal-500/15 tw-text-[11px] tw-font-bold tw-text-teal-700 dark:tw-text-teal-300">{index + 1}</span>
              {label(`steps.${step}`)}
            </li>
          ))}
        </ol>
        <p className="tw-mt-3 tw-text-[11px] tw-leading-snug tw-text-slate-500">{label('note')}</p>
      </Abs>
      {state.opened && <Note rect={A.opened} icon={LuInfo}>{label('opened')}</Note>}
      {state.opened && <span style={{ left: 940, top: top + 456 }} className="tw-absolute tw-text-emerald-500"><LuCheck aria-hidden="true" className="tw-h-5 tw-w-5" /></span>}
    </>
  );
};

AgendaPane.propTypes = { top: PropTypes.number.isRequired, state: PropTypes.object.isRequired };
